import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, KeyRound, ArrowLeft, CheckCircle } from 'lucide-react';
import { forgotPassword, resetPassword } from '../services/api';

const ForgotPasswordPage = () => {
  const [step, setStep] = useState(1); // 1: Enter email, 2: Enter OTP & new password, 3: Success
  const [formData, setFormData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      await forgotPassword({ email: formData.email });
      toast.success('Password reset OTP sent to admin!');
      toast.info('Please contact admin for your verification code');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!formData.otp || !formData.newPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await resetPassword({
        email: formData.email,
        otp: formData.otp,
        new_password: formData.newPassword
      });
      toast.success('Password reset successfully!');
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-primary mb-4">
            <span className="text-primary-foreground font-bold text-xl">FE</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">FenERP</h1>
          <p className="text-muted-foreground text-sm mt-1">Fenestration Business Management</p>
        </div>

        <Card className="border-border shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl flex items-center gap-2">
              {step === 1 && (
                <>
                  <Mail size={20} />
                  Forgot Password
                </>
              )}
              {step === 2 && (
                <>
                  <KeyRound size={20} />
                  Reset Password
                </>
              )}
              {step === 3 && (
                <>
                  <CheckCircle size={20} className="text-green-500" />
                  Password Reset
                </>
              )}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Enter your email to receive a reset code'}
              {step === 2 && 'Enter the OTP from admin and set your new password'}
              {step === 3 && 'Your password has been reset successfully'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 && (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="reset-email"
                    className="mt-1.5"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-accent" 
                  disabled={loading}
                  data-testid="request-reset-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Mail size={18} />
                      Send Reset Code
                    </span>
                  )}
                </Button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="bg-muted/50 p-3 rounded-lg mb-4">
                  <p className="text-sm text-muted-foreground">
                    Resetting password for: <span className="font-medium text-foreground">{formData.email}</span>
                  </p>
                </div>

                <div>
                  <Label htmlFor="otp">Verification Code (OTP)</Label>
                  <Input
                    id="otp"
                    name="otp"
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={formData.otp}
                    onChange={handleChange}
                    maxLength={6}
                    data-testid="reset-otp"
                    className="mt-1.5 text-center text-2xl tracking-widest font-mono"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Contact admin for your verification code
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="newPassword"
                      name="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 6 characters"
                      value={formData.newPassword}
                      onChange={handleChange}
                      data-testid="new-password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm your new password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    data-testid="confirm-new-password"
                    className="mt-1.5"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-accent" 
                  disabled={loading}
                  data-testid="reset-password-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Resetting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <KeyRound size={18} />
                      Reset Password
                    </span>
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setStep(1)}
                >
                  <ArrowLeft size={16} className="mr-2" />
                  Back
                </Button>
              </form>
            )}

            {step === 3 && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <p className="text-muted-foreground">
                  Your password has been reset successfully. You can now login with your new password.
                </p>
                <Button 
                  className="w-full btn-accent" 
                  onClick={() => navigate('/login')}
                  data-testid="go-to-login-btn"
                >
                  Go to Login
                </Button>
              </div>
            )}

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Remember your password? </span>
              <Link to="/login" className="text-accent hover:underline font-medium">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
